import React, { useState, useEffect } from 'react';
import { Save, Loader2, Image, Type, MousePointer } from 'lucide-react';
import { landingPageApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const AdminLandingPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await landingPageApi.get();
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching landing page settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await landingPageApi.update(settings);
      setMessage('Landing page settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving settings');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Landing Page Settings</h1>
        {message && (
          <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList>
            <TabsTrigger value="hero" className="flex items-center">
              <Image className="h-4 w-4 mr-2" />
              Hero Section
            </TabsTrigger>
            <TabsTrigger value="about" className="flex items-center">
              <Type className="h-4 w-4 mr-2" />
              About Section
            </TabsTrigger>
            <TabsTrigger value="cta" className="flex items-center">
              <MousePointer className="h-4 w-4 mr-2" />
              CTA Section
            </TabsTrigger>
          </TabsList>

          {/* Hero Section Tab */}
          <TabsContent value="hero">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Hero Section</h2>
              
              {/* Hero Image */}
              <div className="space-y-2">
                <Label htmlFor="heroImage">Hero Background Image URL</Label>
                <Input
                  id="heroImage"
                  value={settings?.heroImage || ''}
                  onChange={(e) => handleChange('heroImage', e.target.value)}
                  placeholder="https://example.com/hero-image.jpg"
                />
                {settings?.heroImage && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">Preview:</p>
                    <div className="relative h-48 rounded-lg overflow-hidden">
                      <img
                        src={settings.heroImage}
                        alt="Hero preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40" />
                    </div>
                  </div>
                )}
              </div>

              {/* Hero Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="heroTitle">Hero Title (Line 1)</Label>
                  <Input
                    id="heroTitle"
                    value={settings?.heroTitle || ''}
                    onChange={(e) => handleChange('heroTitle', e.target.value)}
                    placeholder="Architectural"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heroTitleHighlight">Hero Title Highlight (Line 2)</Label>
                  <Input
                    id="heroTitleHighlight"
                    value={settings?.heroTitleHighlight || ''}
                    onChange={(e) => handleChange('heroTitleHighlight', e.target.value)}
                    placeholder="Lighting Excellence"
                  />
                </div>
              </div>

              {/* Hero Subtitle */}
              <div className="space-y-2">
                <Label htmlFor="heroSubtitle">Hero Subtitle (Optional - uses company description if empty)</Label>
                <Textarea
                  id="heroSubtitle"
                  rows={3}
                  value={settings?.heroSubtitle || ''}
                  onChange={(e) => handleChange('heroSubtitle', e.target.value)}
                  placeholder="Leave empty to use company description"
                />
              </div>

              {/* Hero Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900">Primary Button</h3>
                  <div className="space-y-2">
                    <Label htmlFor="ctaButtonText">Button Text</Label>
                    <Input
                      id="ctaButtonText"
                      value={settings?.ctaButtonText || ''}
                      onChange={(e) => handleChange('ctaButtonText', e.target.value)}
                      placeholder="Discover More"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ctaButtonLink">Button Link</Label>
                    <Input
                      id="ctaButtonLink"
                      value={settings?.ctaButtonLink || ''}
                      onChange={(e) => handleChange('ctaButtonLink', e.target.value)}
                      placeholder="/about"
                    />
                  </div>
                </div>
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900">Secondary Button</h3>
                  <div className="space-y-2">
                    <Label htmlFor="secondaryButtonText">Button Text</Label>
                    <Input
                      id="secondaryButtonText"
                      value={settings?.secondaryButtonText || ''}
                      onChange={(e) => handleChange('secondaryButtonText', e.target.value)}
                      placeholder="Get in Touch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondaryButtonLink">Button Link</Label>
                    <Input
                      id="secondaryButtonLink"
                      value={settings?.secondaryButtonLink || ''}
                      onChange={(e) => handleChange('secondaryButtonLink', e.target.value)}
                      placeholder="/contact"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                <p className="text-xs text-gray-400 mb-4">HERO PREVIEW</p>
                <h2 className="text-3xl font-light text-white">
                  {settings?.heroTitle || 'Architectural'}
                  <span className="block font-medium">{settings?.heroTitleHighlight || 'Lighting Excellence'}</span>
                </h2>
                <div className="flex gap-3 mt-4">
                  <span className="px-4 py-2 bg-white text-gray-900 text-sm rounded-sm">
                    {settings?.ctaButtonText || 'Discover More'}
                  </span>
                  <span className="px-4 py-2 border border-white/30 text-white text-sm rounded-sm">
                    {settings?.secondaryButtonText || 'Get in Touch'}
                  </span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* About Section Tab */}
          <TabsContent value="about">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">About Section</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="aboutSectionTitle">Section Title (Line 1)</Label>
                  <Input
                    id="aboutSectionTitle"
                    value={settings?.aboutSectionTitle || ''}
                    onChange={(e) => handleChange('aboutSectionTitle', e.target.value)}
                    placeholder="Your Partner in"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aboutSectionTitleHighlight">Section Title Highlight (Line 2)</Label>
                  <Input
                    id="aboutSectionTitleHighlight"
                    value={settings?.aboutSectionTitleHighlight || ''}
                    onChange={(e) => handleChange('aboutSectionTitleHighlight', e.target.value)}
                    placeholder="Architectural Lighting"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aboutSectionText">Additional Text (appears after company description)</Label>
                <Textarea
                  id="aboutSectionText"
                  rows={4}
                  value={settings?.aboutSectionText || ''}
                  onChange={(e) => handleChange('aboutSectionText', e.target.value)}
                  placeholder="Our portfolio includes some of the world's most innovative lighting brands..."
                />
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-4">SECTION PREVIEW</p>
                <h3 className="text-2xl font-light text-gray-900">
                  {settings?.aboutSectionTitle || 'Your Partner in'}
                  <span className="block font-medium">{settings?.aboutSectionTitleHighlight || 'Architectural Lighting'}</span>
                </h3>
                <p className="text-gray-600 mt-4 text-sm">{settings?.aboutSectionText || ''}</p>
              </div>
            </div>
          </TabsContent>

          {/* CTA Section Tab */}
          <TabsContent value="cta">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Bottom CTA Section</h2>
              
              <div className="space-y-2">
                <Label htmlFor="ctaSectionTitle">CTA Title</Label>
                <Input
                  id="ctaSectionTitle"
                  value={settings?.ctaSectionTitle || ''}
                  onChange={(e) => handleChange('ctaSectionTitle', e.target.value)}
                  placeholder="Ready to illuminate your next project?"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ctaSectionText">CTA Description</Label>
                <Textarea
                  id="ctaSectionText"
                  rows={3}
                  value={settings?.ctaSectionText || ''}
                  onChange={(e) => handleChange('ctaSectionText', e.target.value)}
                  placeholder="Let's discuss how our portfolio of world-leading lighting brands can bring your vision to life."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="ctaSectionButtonText">Button Text</Label>
                  <Input
                    id="ctaSectionButtonText"
                    value={settings?.ctaSectionButtonText || ''}
                    onChange={(e) => handleChange('ctaSectionButtonText', e.target.value)}
                    placeholder="Contact Us"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ctaSectionButtonLink">Button Link</Label>
                  <Input
                    id="ctaSectionButtonLink"
                    value={settings?.ctaSectionButtonLink || ''}
                    onChange={(e) => handleChange('ctaSectionButtonLink', e.target.value)}
                    placeholder="/contact"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-6 bg-gray-900 rounded-lg text-center">
                <p className="text-xs text-gray-400 mb-4">CTA PREVIEW</p>
                <h3 className="text-2xl font-light text-white mb-3">
                  {settings?.ctaSectionTitle || 'Ready to illuminate your next project?'}
                </h3>
                <p className="text-gray-400 mb-4 text-sm max-w-xl mx-auto">
                  {settings?.ctaSectionText || "Let's discuss how our portfolio of world-leading lighting brands can bring your vision to life."}
                </p>
                <span className="inline-block px-6 py-2 bg-white text-gray-900 text-sm rounded-sm">
                  {settings?.ctaSectionButtonText || 'Contact Us'}
                </span>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end mt-6">
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save All Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminLandingPage;
